import i18n from '../../i18n/config';
// Enhanced Error Boundary Component with Better Diagnostics
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const safeString = (value, fallback) => {
  if (value === null || value === undefined) return fallback || i18n.t('hardcoded.components_common_errorboundary.not_available');
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return fallback || i18n.t('hardcoded.components_common_errorboundary.not_available');
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorAnalysis: null,
      suspectComponents: []
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  analyzeError(error, errorInfo) {
    const analysis = {
      errorType: i18n.t('hardcoded.components_common_errorboundary.unknown'),
      specificCause: i18n.t('hardcoded.components_common_errorboundary.unknown'),
      suspectedLocation: null,
      suspectedProps: [],
      recommendations: []
    };

    // Safely get error string
    const errorString = safeString(error, '');

    if (errorString.includes('Minified React error #31')) {
      analysis.errorType = 'React Error #31: Invalid Child';
      analysis.specificCause = 'Attempting to render a Promise, Symbol, or non-renderable object as a React child';

      // Try to find what was being rendered
      if (error.stack) {
        // Look for component names in stack
        const stackLines = String(error.stack || '').split('\n');
        const componentRegex = /at (\w+)/g;
        const components = [];

        stackLines.forEach(line => {
          const matches = line.matchAll(componentRegex);
          for (const match of matches) {
            if (match[1] && !match[1].startsWith('Object')) {
              components.push(match[1]);
            }
          }
        });

        analysis.suspectedLocation = components.slice(0, 5);
      }

      // Extract component stack for more details
      if (errorInfo && errorInfo.componentStack) {
        const stack = String(errorInfo.componentStack || '');

        // Look for Typography or p elements
        if (stack.includes(' p\n') || stack.includes('Typography')) {
          analysis.specificCause += ' - Found in a Typography or <p> element';
          analysis.recommendations.push('Check all Typography components and text content');
          analysis.recommendations.push('Verify all translation functions return strings');
          analysis.recommendations.push('Check formatDateTime and other formatting functions');
        }

        // Parse component hierarchy
        const lines = stack.split('\n').filter(line => line && line.trim());
        const hierarchy = [];

        lines.forEach((line, index) => {
          const indent = line.length - line.trimStart().length;
          const component = line.trim().replace(/^at /, '');

          // Check for suspicious patterns
          const suspicious = [];
          if (component === 'p') suspicious.push('HTML paragraph element');
          if (component.includes('Typography')) suspicious.push('Material-UI Typography');
          if (component.includes('Table')) suspicious.push('Table component');
          if (component.includes('Card')) suspicious.push('Card component');

          hierarchy.push({
            level: indent / 4,
            component: component,
            suspicious: suspicious.length > 0,
            notes: suspicious
          });
        });

        analysis.componentHierarchy = hierarchy;
      }

      // Common causes for React Error #31
      analysis.recommendations.push('Check useTranslation hook - it may return Promises');
      analysis.recommendations.push('Verify all API data is properly resolved');
      analysis.recommendations.push('Check all .map() operations return valid JSX');
      analysis.recommendations.push('Ensure conditional rendering doesn\'t return undefined');
      analysis.recommendations.push('Check that all async functions are properly awaited');
    } else if (errorString.includes('history') || errorString.includes('navigate')) {
      analysis.errorType = 'React Router Error';
      analysis.specificCause = 'Router hooks used outside of Router context';
      analysis.recommendations.push('Ensure all components using useNavigate/useLocation are inside <Router>');
      analysis.recommendations.push('Check Provider hierarchy in App.js');
      analysis.recommendations.push('Move TutorialProvider inside Router component');
    }

    return analysis;
  }

  findSuspectComponents(errorInfo) {
    const suspects = [];
    const seen = new Set();

    if (errorInfo && errorInfo.componentStack) {
      const stack = String(errorInfo.componentStack || '');
      const lines = stack.split('\n');

      lines.forEach((line, index) => {
        const trimmedLine = line.trim();
        if (!trimmedLine.startsWith('at ')) return;

        const componentName = trimmedLine.replace(/^at\s+/, '').split(' ')[0];
        const lowerComponent = componentName.toLowerCase();

        const pushSuspect = (suspect) => {
          const key = `${suspect.component}|${suspect.reason}`;
          if (seen.has(key)) return;
          seen.add(key);
          suspects.push(suspect);
        };

        // Flag specific patterns
        if (lowerComponent === 'p') {
          pushSuspect({
            component: 'p element',
            line: index + 1,
            reason: 'Direct p element found - likely rendering non-string content',
            severity: 'HIGH'
          });
        }

        if (componentName.includes('Typography')) {
          pushSuspect({
            component: 'Typography',
            line: index + 1,
            reason: 'Typography component - check children prop',
            severity: 'HIGH'
          });
        }

        if (componentName.includes('TutorialProvider')) {
          pushSuspect({
            component: 'TutorialProvider',
            line: index + 1,
            reason: 'TutorialProvider - check if inside Router',
            severity: 'CRITICAL'
          });
        }

        if (componentName.includes('Table') || componentName.includes('Cell')) {
          pushSuspect({
            component: componentName,
            line: index + 1,
            reason: 'Table component - check cell content rendering',
            severity: 'MEDIUM'
          });
        }
      });
    }

    return suspects;
  }

  componentDidCatch(error, errorInfo) {
    console.error('🚨 SUPER DETAILED ERROR ANALYSIS:', error);
    console.error('🔥 COMPONENT STACK:', errorInfo?.componentStack);
    console.error('🔥 ERROR STACK TRACE:', error?.stack);

    const analysis = this.analyzeError(error, errorInfo);
    const suspects = this.findSuspectComponents(errorInfo);

    // Log detailed analysis
    console.group('🔍 ERROR ANALYSIS DETAILS');
    console.log('Error Type:', analysis.errorType);
    console.log('Specific Cause:', analysis.specificCause);
    console.log('Suspected Components:', analysis.suspectedLocation);
    console.log('Component Hierarchy:', analysis.componentHierarchy);
    console.log('Suspect Components:', suspects);
    console.groupEnd();

    // Check for specific problematic patterns
    const errorString = safeString(error, '');
    if (errorString.includes('#31')) {
      console.group('🎯 REACT ERROR #31 SPECIFIC CHECKS');
      console.log('This error means something non-renderable was passed as a React child.');
      console.log('Common causes:');
      console.log('1. Translation function returning a Promise');
      console.log('2. Async function used directly in JSX');
      console.log('3. Object or Array rendered directly');
      console.log('4. undefined or null in conditional rendering');
      console.groupEnd();
    }

    if (errorString.includes('history') || errorString.includes('navigate')) {
      console.group('🎯 REACT ROUTER ERROR');
      console.log('This error means Router hooks are used outside Router context.');
      console.log('Solution: Move TutorialProvider INSIDE <Router> in App.js');
      console.groupEnd();
    }

    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorAnalysis: analysis,
      suspectComponents: suspects
    });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorAnalysis, suspectComponents } = this.state;
      const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;

      return (
        <Container maxWidth="lg">
          <Box sx={{ py: 4 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                {i18n.t('hardcoded.components_common_errorboundary.react_rendering_error_detected')}</Typography>
              <Typography variant="body2">
                Error ID: <strong>{errorId}</strong>
              </Typography>
            </Alert>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom color="error">
                {i18n.t('hardcoded.components_common_errorboundary.error_analysis')}{safeString(errorAnalysis?.errorType)}
              </Typography>

              <Typography variant="body1" paragraph>
                <strong>{i18n.t('hardcoded.components_common_errorboundary.cause')}:</strong> {safeString(errorAnalysis?.specificCause, i18n.t('hardcoded.components_common_errorboundary.unknown_cause'))}
              </Typography>

              {errorAnalysis?.suspectedLocation && Array.isArray(errorAnalysis.suspectedLocation) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>{i18n.t('hardcoded.components_common_errorboundary.error_location_chain')}</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {errorAnalysis.suspectedLocation.map((comp, idx) => (
                      <Chip
                        key={i18n.t('hardcoded.components_common_errorboundary.loc-idx', { idx: idx })}
                        label={safeString(comp)}
                        color={idx === 0 ? "error" : "default"}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {suspectComponents && suspectComponents.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {i18n.t('hardcoded.components_common_errorboundary.suspect_components')}</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{i18n.t('hardcoded.components_common_errorboundary.component')}</TableCell>
                          <TableCell>{i18n.t('hardcoded.components_schematics_positiontile.reason')}</TableCell>
                          <TableCell>{i18n.t('hardcoded.components_common_errorboundary.severity')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {suspectComponents.map((suspect, idx) => (
                          <TableRow key={`suspect-${idx}`}>
                            <TableCell>
                              <Typography variant="body2" fontFamily="monospace">
                                {safeString(suspect.component)}
                              </Typography>
                            </TableCell>
                            <TableCell>{safeString(suspect.reason)}</TableCell>
                            <TableCell>
                              <Chip
                                label={safeString(suspect.severity, i18n.t('hardcoded.components_common_errorboundary.unknown_severity'))}
                                color={
                                  suspect.severity === 'CRITICAL' ? 'error' :
                                    suspect.severity === 'HIGH' ? 'error' :
                                      'warning'
                                }
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {errorAnalysis?.recommendations && Array.isArray(errorAnalysis.recommendations) && errorAnalysis.recommendations.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <strong>{i18n.t('hardcoded.components_common_errorboundary.debugging_recommendations')}:</strong>
                  </Typography>
                  <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    {errorAnalysis.recommendations.map((rec, idx) => (
                      <li key={i18n.t('hardcoded.components_common_errorboundary.rec-idx', { idx: idx })}>
                        <Typography variant="body2">{safeString(rec, i18n.t('hardcoded.components_common_errorboundary.unknown_recommendation'))}</Typography>
                      </li>
                    ))}
                  </ol>
                </Alert>
              )}

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{i18n.t('hardcoded.components_common_errorboundary.component_stack_trace')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {errorAnalysis?.componentHierarchy && Array.isArray(errorAnalysis.componentHierarchy) && (
                    <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {errorAnalysis.componentHierarchy.map((item, idx) => (
                        <Box
                          key={i18n.t('hardcoded.components_common_errorboundary.hier-idx', { idx: idx })}
                          sx={{
                            pl: (item.level || 0) * 2,
                            py: 0.5,
                            bgcolor: item.suspicious ? 'error.light' : 'transparent'
                          }}
                        >
                          {'  '.repeat(item.level || 0)}↳ {safeString(item.component, i18n.t('hardcoded.components_common_errorboundary.unknown'))}
                          {item.notes && Array.isArray(item.notes) && item.notes.length > 0 && (
                            <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                              ({item.notes.map(n => safeString(n, '')).join(', ')})
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{
                      mt: 2,
                      p: 1,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto'
                    }}
                  >
                    {safeString(errorInfo?.componentStack, i18n.t('hardcoded.components_common_errorboundary.no_component_stack'))}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{i18n.t('hardcoded.components_common_errorboundary.full_error_details')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" gutterBottom>
                    <strong>{i18n.t('hardcoded.components_common_errorboundary.error_message')}</strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{
                      p: 1,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      mb: 2
                    }}
                  >
                    {safeString(error?.toString(), i18n.t('hardcoded.components_common_errorboundary.no_error_message'))}
                  </Typography>

                  <Typography variant="body2" gutterBottom>
                    <strong>{i18n.t('hardcoded.components_common_errorboundary.stack_trace')}</strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{
                      p: 1,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 300
                    }}
                  >
                    {safeString(error?.stack, i18n.t('hardcoded.components_common_errorboundary.no_stack_trace'))}
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={this.handleRefresh}
                  startIcon={<RefreshIcon />}
                >
                  {i18n.t('hardcoded.components_common_errorboundary.refresh_page')}</Button>
              </Box>
            </Paper>

            <Alert severity="warning">
              <Typography variant="body2">
                <strong>{i18n.t('hardcoded.components_common_errorboundary.quick_fix_checklist')}:</strong>
              </Typography>
              <ul style={{ margin: '8px 0' }}>
                <li>{i18n.t('hardcoded.components_common_errorboundary.fix_check_translations')}</li>
                <li>{i18n.t('hardcoded.components_common_errorboundary.fix_check_formatdatetime')}</li>
                <li>{i18n.t('hardcoded.components_common_errorboundary.fix_check_conditional_rendering')}</li>
                <li>{i18n.t('hardcoded.components_common_errorboundary.fix_check_api_data')}</li>
                <li>{i18n.t('hardcoded.components_common_errorboundary.fix_check_map_operations')}</li>
                <li><strong>{i18n.t('hardcoded.components_common_errorboundary.critical_ensure_tutorialprovider_is_inside')}</strong></li>
              </ul>
            </Alert>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;